import { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Mail, Paperclip, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { Reveal } from "./Reveal";
import { Newsletter } from "./Newsletter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", company: "", email: "", interest: c.f.interests[0], message: "" });
  const [sending, setSending] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const okType = /^(application\/pdf|text\/csv)$/.test(f.type) || /\.(pdf|csv)$/i.test(f.name);
    if (!okType || f.size > 10 * 1024 * 1024) {
      toast.error(c.f.attachBad);
      return;
    }
    setFile(f);
  };

  useEffect(() => {
    const listen = (e) => {
      const i = e.detail;
      setForm((f) => ({ ...f, interest: t.contact.f.interests[i] ?? f.interest }));
    };
    window.addEventListener("hadron:interest", listen);
    return () => window.removeEventListener("hadron:interest", listen);
  }, [t]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!form.name.trim() || !emailOk || !form.message.trim()) {
      toast.error(c.f.errRequired, { "data-testid": "contact-form-error" });
      return;
    }
    setUploading(true);
    let attachment = null;
    if (file) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const up = await fetch(`${API}/uploads`, { method: "POST", body: fd });
        if (!up.ok) throw new Error("upload");
        attachment = await up.json();
      } catch {
        toast.error(c.f.attachBad, { "data-testid": "contact-form-error" });
        setUploading(false);
        return;
      }
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          product_interest: form.interest,
          language: lang,
          attachment_name: (attachment && attachment.filename) || "",
          attachment_path: (attachment && attachment.path) || "",
          attachment_size: (attachment && attachment.size) || 0,
        }),
      });
      if (!res.ok) throw new Error("submit");
      toast.success(c.f.success, { "data-testid": "contact-form-success" });
      setForm((f) => ({ ...f, name: "", company: "", email: "", message: "" }));
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch {
      toast.error(c.f.errorGeneric, { "data-testid": "contact-form-error" });
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const inputCls =
    "w-full bg-ink border border-line px-4 py-3.5 text-sm text-txt placeholder:text-txt-3 focus:outline-none focus:border-hadron-cyan focus:ring-1 focus:ring-hadron-cyan/40 transition-colors";

  return (
    <section id="contact" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-12">
        {/* info */}
        <div className="lg:col-span-5">
          <Reveal>
            <p className="mono-label">{c.eyebrow}</p>
            <h2 className="mt-4 font-display uppercase font-bold tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl">
              {c.heading}
            </h2>
            <p className="mt-6 max-w-md text-txt-2 leading-relaxed">{c.lead}</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 space-y-5">
            <a data-testid="contact-info-address" className="flex items-start gap-4 group" href="https://maps.google.com/?q=Podolany+88+32-420+Gdów+Poland" target="_blank" rel="noreferrer">
              <span className="grid place-items-center h-11 w-11 border border-line text-hadron-cyan group-hover:border-hadron-cyan transition-colors shrink-0"><MapPin size={17} /></span>
              <span>
                <span className="block mono-label !text-[9px]">{c.addrLabel}</span>
                <span className="block mt-1.5 text-txt">Hadron Energy Poland sp. z o.o.<br />Podolany 88, 32-420 Gdów, Poland</span>
              </span>
            </a>
            <a data-testid="contact-info-phone" className="flex items-start gap-4 group" href="tel:+48122002588">
              <span className="grid place-items-center h-11 w-11 border border-line text-hadron-cyan group-hover:border-hadron-cyan transition-colors shrink-0"><Phone size={17} /></span>
              <span>
                <span className="block mono-label !text-[9px]">{c.phoneLabel}</span>
                <span className="block mt-1.5 text-txt group-hover:text-hadron-cyan transition-colors">+48 12 2002 588</span>
              </span>
            </a>
            <a data-testid="contact-info-email" className="flex items-start gap-4 group" href="mailto:contact@hadronenergy.pl">
              <span className="grid place-items-center h-11 w-11 border border-line text-hadron-cyan group-hover:border-hadron-cyan transition-colors shrink-0"><Mail size={17} /></span>
              <span>
                <span className="block mono-label !text-[9px]">{c.emailLabel}</span>
                <span className="block mt-1.5 text-txt group-hover:text-hadron-cyan transition-colors break-all">contact@hadronenergy.pl</span>
              </span>
            </a>
          </Reveal>
        </div>

        {/* form */}
        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} className="card-surface p-6 sm:p-10" data-testid="contact-form" noValidate>
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="mono-label !text-[9px]">{c.f.name} *</span>
                <input data-testid="contact-name-input" className={`${inputCls} mt-2`} value={form.name} onChange={set("name")} placeholder="Jan Kowalski" />
              </label>
              <label className="block">
                <span className="mono-label !text-[9px]">{c.f.company}</span>
                <input data-testid="contact-company-input" className={`${inputCls} mt-2`} value={form.company} onChange={set("company")} placeholder="Sp. z o.o. / GmbH / Ltd." />
              </label>
              <label className="block">
                <span className="mono-label !text-[9px]">{c.f.email} *</span>
                <input data-testid="contact-email-input" type="email" className={`${inputCls} mt-2`} value={form.email} onChange={set("email")} placeholder="you@company.eu" />
              </label>
              <label className="block">
                <span className="mono-label !text-[9px]">{c.f.interest}</span>
                <select data-testid="contact-interest-select" className={`${inputCls} mt-2 appearance-none`} value={form.interest} onChange={set("interest")}>
                  {c.f.interests.map((o) => <option key={o} value={o} className="bg-ink text-txt">{o}</option>)}
                </select>
              </label>
            </div>
            <label className="block mt-5">
              <span className="mono-label !text-[9px]">{c.f.msg} *</span>
              <textarea data-testid="contact-message-input" rows={5} className={`${inputCls} mt-2 resize-none`} value={form.message} onChange={set("message")} />
            </label>
            <input
              data-testid="contact-attachment-input"
              ref={fileRef}
              type="file"
              accept=".pdf,.csv,application/pdf,text/csv"
              className="hidden"
              onChange={pick}
            />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                data-testid="contact-attachment-button"
                onClick={() => fileRef.current && fileRef.current.click()}
                className="inline-flex items-center gap-2.5 border border-line px-4 py-3 font-mono2 text-[10px] uppercase tracking-[0.18em] text-txt-2 hover:text-hadron-cyan hover:border-hadron-cyan transition-colors"
              >
                <Paperclip size={13} />
                {c.f.attach} · {c.f.attachSize}
              </button>
              {file && (
                <span data-testid="contact-attachment-name" className="flex items-center gap-2 text-xs text-txt-2">
                  <FileText size={13} className="text-hadron-green" />
                  {file.name} · {(file.size / 1024).toFixed(0)} KB
                  <button type="button" onClick={() => setFile(null)} className="text-txt-3 hover:text-hadron-cyan" aria-label="remove attachment">
                    <X size={13} />
                  </button>
                </span>
              )}
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending || uploading}
              className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-hadron-cyan clip-frame px-10 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-ink font-medium hover:bg-hadron-green transition-colors disabled:opacity-60"
            >
              {sending ? c.f.sending : c.f.submit}
            </button>
          </form>
          <Newsletter source="contact" />
        </Reveal>
      </div>
    </section>
  );
};
